import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Save as SaveIcon, Publish as PublishIcon, CloudDownload as CloudDownloadIcon, NoteAdd as NoteAddIcon } from '@mui/icons-material';
import PDFCanvas, { PlacedField } from './PDFCanvas';
import FieldPalette from './FieldPalette';
import FieldPropertyPanel from './FieldPropertyPanel';
import { ImportDialog } from './ImportDialog';
import { tariffApi, templateApi, publishingApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import { convertDocuSignToDocumenso, ReplacementVariable, EventData } from '../utils/documensoConverter';
import {
  DocuSignTemplate,
  DocuSignBaseTab,
  UIVariantType,
  UI_VARIANT_LABELS,
  uiVariantToDbKey,
  Tariff,
  TemplateVersion,
} from '../types';
import ToothSpinner from './ToothSpinner';

function TemplateEditor() {
  const { tariffId, variantType: uiVariant } = useParams<{ tariffId: string; variantType: UIVariantType }>();
  const dbVariantKey = uiVariant ? uiVariantToDbKey(uiVariant) : undefined;
  const navigate = useNavigate();
  const { userEmail } = useAuth();

  // State
  const [tariff, setTariff] = useState<Tariff | null>(null);
  const [template, setTemplate] = useState<DocuSignTemplate | null>(null);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Array<{ fieldLabel: string; page: number; tabType: string; tabIndex: number; message: string }>>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedFieldType, setSelectedFieldType] = useState<string | null>(null);
  const [fieldDefaults, setFieldDefaults] = useState<{ width: number; height: number; fontSize: number } | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [replacementVariables, setReplacementVariables] = useState<ReplacementVariable[] | undefined>(undefined);
  const [eventData, setEventData] = useState<EventData | undefined>(undefined);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);

  // Warn user about unsaved changes when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Load template on mount
  useEffect(() => {
    if (tariffId && uiVariant) {
      loadTemplate();
    }
  }, [tariffId, uiVariant]);

  const loadTemplate = async () => {
    if (!tariffId || !dbVariantKey) return;

    try {
      setLoading(true);
      setError(null);

      // Load tariff data
      const tariffData = await tariffApi.getTariff(parseInt(tariffId));
      setTariff(tariffData.tariff);

      // Load all versions for this tariff/variant
      try {
        const versionsData = await templateApi.getTemplatesByTariffAndVariant(
          parseInt(tariffId),
          dbVariantKey
        );
        setVersions(versionsData.templates.sort((a, b) => b.version - a.version));

        // Load the published version if it exists, otherwise the latest draft
        const publishedVersion = versionsData.templates.find(v => v.status === 'published');
        const latestVersion = versionsData.templates[0];
        const versionToLoad = publishedVersion || latestVersion;

        if (versionToLoad) {
          const templateData = await templateApi.getTemplate(versionToLoad.templateId);
          setTemplate(templateData.template);
          setCurrentVersion(versionToLoad.version);
        } else {
          // No template exists, create a new one
          createNewTemplate();
        }
      } catch (versionsErr: any) {
        // If no templates exist (404), create a new one
        if (versionsErr?.response?.status === 404 || versionsErr?.message?.includes('404')) {
          console.log('No existing templates found, creating new template');
          createNewTemplate();
        } else {
          throw versionsErr;
        }
      }
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const createNewTemplate = () => {
    if (!tariffId || !uiVariant) return;

    // Create a minimal template structure
    const newTemplate: DocuSignTemplate = {
      templateId: crypto.randomUUID(),
      name: `Tariff ${tariffId} - ${uiVariant}`,
      pageCount: '0',
      documents: [],
      recipients: {
        signers: [
          {
            recipientId: '1',
            tabs: {},
          },
        ],
      },
    };

    setTemplate(newTemplate);
    setCurrentVersion(null);
    setIsDirty(true);
  };

  const handlePDFUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data:application/pdf;base64, prefix
        const base64Data = base64.split(',')[1];

        if (template) {
          // Update existing template with PDF
          const updatedTemplate: DocuSignTemplate = {
            ...template,
            documents: [
              {
                documentId: '1',
                name: file.name,
                documentBase64: base64Data,
                order: '1',
                pages: '1',
              },
            ],
            pageCount: '1', // Will be updated when PDF is loaded
          };
          setTemplate(updatedTemplate);
          setIsDirty(true);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Failed to upload PDF');
    }
  };

  const handleVersionChange = async (version: number) => {
    if (isDirty) {
      // Warn about unsaved changes
      const confirmed = window.confirm(
        'You have unsaved changes. Loading a different version will discard them. Continue?'
      );
      if (!confirmed) return;
    }

    const versionToLoad = versions.find(v => v.version === version);
    if (!versionToLoad) return;

    try {
      const templateData = await templateApi.getTemplate(versionToLoad.templateId, version);
      setTemplate(templateData.template);
      setCurrentVersion(version);
      setIsDirty(false);
      setSelectedFieldId(null);
    } catch (err) {
      console.error('Error loading version:', err);
      setError('Failed to load version');
    }
  };

  const handleSave = async () => {
    if (!template || !tariffId || !uiVariant || !dbVariantKey) return;

    if (!userEmail) {
      setError('User email not available. Please log in again.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Update template metadata
      const updatedTemplate = {
        ...template,
        lastModified: new Date().toISOString(),
      };

      // Save using the primary DB key — backend handles KIDS dual-write automatically
      // (both DIFFERENT_EMAIL_KIDS and DIFFERENT_POST_KIDS share the same UUID and S3 object)
      const result = await templateApi.saveTemplate(
        parseInt(tariffId),
        dbVariantKey,
        updatedTemplate,
        userEmail
      );

      // Update template with the new templateId from the backend
      setTemplate({
        ...updatedTemplate,
        templateId: result.templateId,
      });

      // Reload versions (use primary DB key)
      const versionsData = await templateApi.getTemplatesByTariffAndVariant(
        parseInt(tariffId),
        dbVariantKey
      );
      setVersions(versionsData.templates.sort((a, b) => b.version - a.version));
      setCurrentVersion(result.version);

      alert(`Template saved as version ${result.version}`);
      setIsDirty(false);
    } catch (err: any) {
      console.error('Error saving template:', err);
      const apiError = err?.response?.data?.error;
      const apiMessage = apiError?.message;
      const apiFieldErrors = apiError?.details?.fieldErrors;
      if (apiFieldErrors && Array.isArray(apiFieldErrors)) {
        setFieldErrors(apiFieldErrors);
        setError('Save failed — see errors below:');
      } else {
        setFieldErrors([]);
        setError(apiMessage ? `Save failed: ${apiMessage}` : 'Failed to save template');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!template || !currentVersion) {
      alert('Please save the template before publishing');
      return;
    }

    if (!userEmail) {
      setError('User email not available. Please log in again.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to publish version ${currentVersion}? This will make it the active template.`
    );
    if (!confirmed) return;

    try {
      setPublishing(true);
      setError(null);

      await publishingApi.publishVersion(template.templateId, currentVersion, userEmail);

      // Reload versions
      const versionsData = await templateApi.getTemplatesByTariffAndVariant(
        parseInt(tariffId!),
        dbVariantKey!
      );
      setVersions(versionsData.templates.sort((a, b) => b.version - a.version));

      alert(`Version ${currentVersion} published successfully`);
    } catch (err) {
      console.error('Error publishing template:', err);
      setError('Failed to publish template');
    } finally {
      setPublishing(false);
    }
  };

  const handleFieldTypeSelect = (fieldType: string, defaults: { width: number; height: number; fontSize: number }) => {
    setSelectedFieldType(fieldType);
    setFieldDefaults(defaults);
  };

  const handleFieldDragStart = (fieldType: string, defaults: { width: number; height: number; fontSize: number }) => {
    setSelectedFieldType(fieldType);
    setFieldDefaults(defaults);
  };

  const handleFieldPlace = (field: Omit<PlacedField, 'id'>) => {
    if (!template) return;

    const updatedTemplate = { ...template };
    const signer = updatedTemplate.recipients.signers[0];

    // Handle radio buttons specially
    if (field.tabType === 'radioGroupTabs') {
      // Create new radio group with one default radio button
      if (!signer.tabs.radioGroupTabs) {
        signer.tabs.radioGroupTabs = [];
      }

      const groupName = `RadioGroup_${signer.tabs.radioGroupTabs.length + 1}`;
      
      const radioGroup = {
        documentId: '1',
        recipientId: '1',
        templateLocked: 'false',
        templateRequired: 'false',
        groupName,
        radios: [
          {
            pageNumber: field.pageNumber.toString(),
            xPosition: field.xPosition.toString(),
            yPosition: field.yPosition.toString(),
            value: 'Option 1',
            selected: 'false',
            tabId: crypto.randomUUID(),
            required: 'false',
            locked: 'true',
            font: 'lucidaconsole',
            bold: 'false',
            italic: 'false',
            underline: 'false',
            fontColor: 'black',
            fontSize: 'size9',
          },
        ],
        shared: 'false',
        requireInitialOnSharedChange: 'false',
        requireAll: 'false',
        tabType: 'radiogroup',
        value: '',
        originalValue: '',
        source: 'Template',
      };
      
      signer.tabs.radioGroupTabs.push(radioGroup);

      setTemplate(updatedTemplate);
      setIsDirty(true);
      setSelectedFieldType(null);
      setFieldDefaults(null);
      
      // Auto-select the first radio button of the new group
      setSelectedFieldId(`0-radioGroupTabs-${signer.tabs.radioGroupTabs.length - 1}-0`);
      return;
    }

    // Handle other field types
    // Determine tab label
    let tabLabel = field.tabLabel;
    if (!tabLabel) {
      // Auto-generate label based on field type
      const existingCount = countFieldsOfType(field.tabType);
      tabLabel = `${field.tabType}_${existingCount + 1}`;
    }

    // Create the new tab
    const newTab: DocuSignBaseTab = {
      tabLabel,
      documentId: '1',
      recipientId: '1',
      pageNumber: field.pageNumber.toString(),
      xPosition: field.xPosition.toString(),
      yPosition: field.yPosition.toString(),
      width: field.width.toString(),
      height: field.height.toString(),
    };

    // Add type-specific properties
    if (field.tabType === 'textTabs') {
      (newTab as any).fontSize = field.fontSize?.toString() || '12';
      (newTab as any).required = 'false';
      (newTab as any).locked = field.locked?.toString() || 'false';
    } else if (field.tabType === 'signHereTabs') {
      (newTab as any).stampType = 'signature';
      (newTab as any).scaleValue = '1';
      (newTab as any).optional = 'false';
    } else if (field.tabType === 'checkboxTabs') {
      (newTab as any).selected = 'false';
      (newTab as any).required = 'false';
    }

    // Add the tab to the template
    if (!signer.tabs[field.tabType as keyof typeof signer.tabs]) {
      (signer.tabs as any)[field.tabType] = [];
    }
    
    (signer.tabs as any)[field.tabType].push(newTab);

    setTemplate(updatedTemplate);
    setIsDirty(true);
    setSelectedFieldType(null);
    setFieldDefaults(null);
  };

  const countFieldsOfType = (tabType: string): number => {
    if (!template) return 0;
    
    let count = 0;
    template.recipients.signers.forEach(signer => {
      const tabs = (signer.tabs as any)[tabType];
      if (tabs && Array.isArray(tabs)) {
        count += tabs.length;
      }
    });
    return count;
  };

  const handleFieldUpdate = (id: string, updates: Partial<PlacedField>) => {
    if (!template) return;

    // Find and update the field
    const updatedTemplate = { ...template };
    let found = false;

    updatedTemplate.recipients.signers.forEach((signer, signerIdx) => {
      Object.entries(signer.tabs).forEach(([tabType, tabs]) => {
        if (tabType === 'radioGroupTabs' && tabs && Array.isArray(tabs)) {
          // Handle radio buttons
          tabs.forEach((group: any, groupIdx) => {
            group.radios?.forEach((radio: any, radioIdx: number) => {
              const fieldId = `${signerIdx}-${tabType}-${groupIdx}-${radioIdx}`;
              if (fieldId === id) {
                if (updates.xPosition !== undefined) radio.xPosition = updates.xPosition.toString();
                if (updates.yPosition !== undefined) radio.yPosition = updates.yPosition.toString();
                if (updates.pageNumber !== undefined) radio.pageNumber = updates.pageNumber.toString();
                found = true;
              }
            });
          });
        } else if (tabs && Array.isArray(tabs)) {
          // Handle other field types
          tabs.forEach((tab, tabIdx) => {
            const fieldId = `${signerIdx}-${tabType}-${tabIdx}`;
            if (fieldId === id) {
              if (updates.xPosition !== undefined) tab.xPosition = updates.xPosition.toString();
              if (updates.yPosition !== undefined) tab.yPosition = updates.yPosition.toString();
              if (updates.width !== undefined) tab.width = updates.width.toString();
              if (updates.height !== undefined) tab.height = updates.height.toString();
              if (updates.pageNumber !== undefined) tab.pageNumber = updates.pageNumber.toString();
              found = true;
            }
          });
        }
      });
    });

    if (found) {
      setTemplate(updatedTemplate);
      setIsDirty(true);
    }
  };

  const handleFieldSelect = (id: string | null) => {
    setSelectedFieldId(id);
  };

  const handleFieldDelete = (id: string) => {
    if (!template) return;

    const updatedTemplate = { ...template };
    let found = false;

    updatedTemplate.recipients.signers.forEach((signer, signerIdx) => {
      Object.entries(signer.tabs).forEach(([tabType, tabs]) => {
        if (tabType === 'radioGroupTabs' && tabs && Array.isArray(tabs)) {
          // Handle radio buttons
          tabs.forEach((group: any, groupIdx) => {
            const fieldId = `${signerIdx}-${tabType}-${groupIdx}`;
            if (id.startsWith(fieldId)) {
              const radioIdx = parseInt(id.split('-')[3]);
              group.radios.splice(radioIdx, 1);
              // If no radios left, remove the group
              if (group.radios.length === 0) {
                tabs.splice(groupIdx, 1);
              }
              found = true;
            }
          });
        } else if (tabs && Array.isArray(tabs)) {
          // Handle other field types
          const fieldId = `${signerIdx}-${tabType}`;
          if (id.startsWith(fieldId)) {
            const tabIdx = parseInt(id.split('-')[2]);
            tabs.splice(tabIdx, 1);
            found = true;
          }
        }
      });
    });

    if (found) {
      setTemplate(updatedTemplate);
      setIsDirty(true);
      setSelectedFieldId(null);
    }
  };

  const handleFieldPropertyUpdate = (
    signerIndex: number,
    tabType: string,
    tabIndex: number,
    updates: Partial<DocuSignBaseTab>
  ) => {
    if (!template) return;

    const updatedTemplate = { ...template };
    const signer = updatedTemplate.recipients.signers[signerIndex];
    const tabs = (signer.tabs as any)[tabType];
    
    if (tabs && Array.isArray(tabs) && tabs[tabIndex]) {
      Object.assign(tabs[tabIndex], updates);
      setTemplate(updatedTemplate);
      setIsDirty(true);
    }
  };

  const handleAddRadioValue = (groupIndex: number, value: string) => {
    if (!template) return;

    const updatedTemplate = { ...template };
    const signer = updatedTemplate.recipients.signers[0];
    const radioGroups = signer.tabs.radioGroupTabs;

    if (radioGroups && radioGroups[groupIndex]) {
      const group = radioGroups[groupIndex];
      const lastRadio = group.radios[group.radios.length - 1];
      
      // Add new radio button next to the last one (offset by 30 pixels)
      group.radios.push({
        pageNumber: lastRadio.pageNumber,
        xPosition: (parseFloat(lastRadio.xPosition) + 30).toString(),
        yPosition: lastRadio.yPosition,
        value,
        selected: 'false',
        tabId: crypto.randomUUID(),
        required: 'false',
        locked: 'true',
        font: 'lucidaconsole',
        bold: 'false',
        italic: 'false',
        underline: 'false',
        fontColor: 'black',
        fontSize: 'size9',
      });

      setTemplate(updatedTemplate);
      setIsDirty(true);
    }
  };

  const handleRemoveRadioValue = (groupIndex: number, radioIndex: number) => {
    if (!template) return;

    const updatedTemplate = { ...template };
    const signer = updatedTemplate.recipients.signers[0];
    const radioGroups = signer.tabs.radioGroupTabs;

    if (radioGroups && radioGroups[groupIndex]) {
      const group = radioGroups[groupIndex];
      
      // Don't allow removing the last radio button
      if (group.radios.length > 1) {
        group.radios.splice(radioIndex, 1);
        setTemplate(updatedTemplate);
        setIsDirty(true);
      }
    }
  };

  const handleNavigateBack = () => {
    if (isDirty) {
      setUnsavedDialogOpen(true);
      setPendingNavigation(`/tariff/${tariffId}`);
    } else {
      navigate(`/tariff/${tariffId}`);
    }
  };

  const handleConfirmNavigation = () => {
    setUnsavedDialogOpen(false);
    if (pendingNavigation) {
      setIsDirty(false);
      navigate(pendingNavigation);
    }
  };

  const handleCancelNavigation = () => {
    setUnsavedDialogOpen(false);
    setPendingNavigation(null);
  };

  // Convert template fields to PlacedField format for PDFCanvas
  const placedFields = useMemo(() => {
    if (!template) return [];

    const fields: PlacedField[] = [];
    template.recipients.signers.forEach((signer, signerIdx) => {
      Object.entries(signer.tabs).forEach(([tabType, tabs]) => {
        if (tabType === 'radioGroupTabs' && tabs && Array.isArray(tabs)) {
          // Handle radio groups - convert each radio button to a PlacedField
          tabs.forEach((group: any, groupIdx) => {
            group.radios?.forEach((radio: any, radioIdx: number) => {
              fields.push({
                id: `${signerIdx}-${tabType}-${groupIdx}-${radioIdx}`,
                tabLabel: `${group.groupName}: ${radio.value}`,
                tabType,
                pageNumber: parseInt(radio.pageNumber),
                xPosition: parseFloat(radio.xPosition),
                yPosition: parseFloat(radio.yPosition),
                width: 20,
                height: 20,
                fontSize: 9,
                radioGroupName: group.groupName,
                radioValue: radio.value,
              });
            });
          });
        } else if (tabs && Array.isArray(tabs)) {
          // Handle other field types
          tabs.forEach((tab, tabIdx) => {
            fields.push({
              id: `${signerIdx}-${tabType}-${tabIdx}`,
              tabLabel: tab.tabLabel,
              tabType,
              pageNumber: parseInt(tab.pageNumber),
              xPosition: parseFloat(tab.xPosition),
              yPosition: parseFloat(tab.yPosition),
              width: parseFloat(tab.width || '100'),
              height: parseFloat(tab.height || '20'),
              fontSize: parseFloat((tab as any).fontSize || '12'),
            });
          });
        }
      });
    });

    return fields;
  }, [template]);

  // Get selected field for property panel
  const selectedFieldForPanel = useMemo(() => {
    if (!selectedFieldId || !template) return null;

    const parts = selectedFieldId.split('-');
    const signerIdx = parts[0];
    const tabType = parts[1];
    const tabIdx = parts[2];
    
    return {
      tabType,
      tabIndex: parseInt(tabIdx),
      signerIndex: parseInt(signerIdx),
    };
  }, [selectedFieldId, template]);

  const currentVersionStatus = useMemo(() => {
    if (!currentVersion) return null;
    const version = versions.find(v => v.version === currentVersion);
    return version?.status || null;
  }, [currentVersion, versions]);

  // Convert template to Documenso format
  const documensoConversion = useMemo(() => {
    if (!template || !template.documents[0]?.documentBase64) {
      return {
        success: false,
        error: 'No template or PDF loaded',
      };
    }

    // Use standard PDF dimensions (A4 at 72 DPI)
    // These are approximate - in production, you'd get actual dimensions from the PDF
    const pdfDimensions = {
      width: 595,  // A4 width in points at 72 DPI
      height: 842, // A4 height in points at 72 DPI
    };

    return convertDocuSignToDocumenso(template, pdfDimensions, replacementVariables, eventData);
  }, [template, replacementVariables, eventData]);

  // Callback for when replacement variables change in the export panel
  const handleReplacementVariablesChange = useCallback((variables: ReplacementVariable[] | undefined) => {
    setReplacementVariables(variables);
  }, []);

  // Callback for when event data changes in the export panel
  const handleEventDataChange = useCallback((data: EventData | undefined) => {
    setEventData(data);
  }, []);

  const handleDeleteAllFields = useCallback(() => {
    if (!template) return;
    const updatedTemplate = { ...template };
    updatedTemplate.recipients.signers.forEach(signer => {
      signer.tabs = {};
    });
    setTemplate(updatedTemplate);
    setIsDirty(true);
    setSelectedFieldId(null);
  }, [template]);

  const handleImportSuccess = useCallback(() => {
    // Reload the template list after successful import
    if (tariffId && uiVariant) {
      loadTemplate();
    }
  }, [tariffId, uiVariant]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ToothSpinner />
      </Box>
    );
  }

  if (error && !template) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
          {/* Breadcrumbs */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                Tariffs
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={handleNavigateBack}
                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                Tariff {tariffId}
              </Link>
              <Typography variant="body2" color="text.primary" fontWeight={500}>
                {uiVariant && UI_VARIANT_LABELS[uiVariant]}
              </Typography>
            </Breadcrumbs>

            {/* Status chips */}
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
              {currentVersionStatus && (
                <Chip
                  label={currentVersionStatus}
                  color={currentVersionStatus === 'published' ? 'success' : 'warning'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {isDirty && (
                <Chip
                  label="Unsaved"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </Box>

          {/* Version Selector */}
          {versions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 140 }} key={`version-selector-${versions.length}-${currentVersion}`}>
              <InputLabel>Version</InputLabel>
              <Select
                value={currentVersion || ''}
                onChange={(e) => handleVersionChange(e.target.value as number)}
                label="Version"
                sx={{ fontSize: '0.85rem' }}
              >
                {versions.map((version) => (
                  <MenuItem key={version.version} value={version.version}>
                    v{version.version} ({version.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<NoteAddIcon />}
              component="label"
              size="small"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Replace PDF
              <input type="file" hidden accept="application/pdf" onChange={handlePDFUpload} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={() => setImportDialogOpen(true)}
              size="small"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!isDirty || saving}
              size="small"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
              disabled={!currentVersion || publishing || currentVersionStatus === 'published'}
              size="small"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => { setError(null); setFieldErrors([]); }} sx={{ mx: 2, mt: 2, ml: leftPanelOpen ? '296px' : 2, mr: rightPanelOpen ? '336px' : 2, transition: 'margin 225ms cubic-bezier(0, 0, 0.2, 1)', wordBreak: 'break-word' }}>
          {error}
          {fieldErrors.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              {fieldErrors.map((fe, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, mb: 0.25, fontSize: '0.85rem' }}
                  onClick={() => {
                    const fieldId = `0-${fe.tabType}-${fe.tabIndex}`;
                    setSelectedFieldId(fieldId);
                    // Scroll to the field element after a short delay for render
                    setTimeout(() => {
                      const el = document.querySelector(`[data-field-id="${fieldId}"]`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                  }}
                >
                  {fe.message}
                </Box>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', bgcolor: '#f8f9fa', position: 'relative' }}>
        {/* Field Palette */}
        <FieldPalette
          template={template}
          selectedFieldType={selectedFieldType}
          onFieldTypeSelect={handleFieldTypeSelect}
          onFieldDragStart={handleFieldDragStart}
          onDeleteAllFields={handleDeleteAllFields}
          isOpen={leftPanelOpen}
          onToggle={() => setLeftPanelOpen(prev => !prev)}
        />

        {/* PDF Canvas */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: leftPanelOpen ? 280 : 0,
            right: rightPanelOpen ? 320 : 0,
            overflow: 'auto',
            px: 2,
            transition: 'left 225ms cubic-bezier(0, 0, 0.2, 1), right 225ms cubic-bezier(0, 0, 0.2, 1)',
          }}
        >
          {template && template.documents.length > 0 && template.documents[0].documentBase64 ? (
            <PDFCanvas
              pdfBase64={template.documents[0].documentBase64}
              selectedFieldType={selectedFieldType}
              fieldDefaults={fieldDefaults || undefined}
              placedFields={placedFields}
              selectedFieldId={selectedFieldId}
              onFieldPlace={handleFieldPlace}
              onFieldUpdate={handleFieldUpdate}
              onFieldSelect={handleFieldSelect}
              onFieldDelete={handleFieldDelete}
            />
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
              gap={2}
              sx={{ backgroundColor: '#f5f5f5' }}
            >
              <Alert severity="info">
                No PDF document loaded. Please upload a PDF to begin editing.
              </Alert>
              <Button
                variant="contained"
                component="label"
              >
                Upload PDF
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={handlePDFUpload}
                />
              </Button>
            </Box>
          )}
        </Box>

        {/* Field Property Panel */}
        <FieldPropertyPanel
          template={template}
          selectedField={selectedFieldForPanel}
          onFieldUpdate={handleFieldPropertyUpdate}
          onAddRadioValue={handleAddRadioValue}
          onRemoveRadioValue={handleRemoveRadioValue}
          documensoConversion={documensoConversion}
          onReplacementVariablesChange={handleReplacementVariablesChange}
          onEventDataChange={handleEventDataChange}
          templateId={template?.templateId || ''}
          version={currentVersion || 0}
          isOpen={rightPanelOpen}
          onToggle={() => setRightPanelOpen(prev => !prev)}
        />
      </Box>

      {/* Unsaved Changes Dialog */}
      <Dialog open={unsavedDialogOpen} onClose={handleCancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>Cancel</Button>
          <Button onClick={handleConfirmNavigation} color="error" autoFocus>
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      {tariffId && dbVariantKey && uiVariant && (
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          tariffId={parseInt(tariffId)}
          variantType={dbVariantKey}
          currentTemplateId={tariff?.automationProviderSettings?.templateId?.[dbVariantKey]}
          onImportSuccess={handleImportSuccess}
          currentTariffId={parseInt(tariffId)}
          currentUiVariant={uiVariant as UIVariantType}
        />
      )}
    </Box>
  );
}

export default TemplateEditor;
